import { useEffect, useRef } from 'react';
import type { AppState } from '../state/types.js';
import { useAppState } from './context.js';

/**
 * Where keyboard focus goes after the screen changes (03 §5.2). The prototype lets focus fall back to
 * the document on almost every change; these rules are the pack's implementation default, plus a few
 * for cases the pack's table does not list (marked "this build").
 *
 * | After | Focus |
 * |---|---|
 * | A day opens | The day title |
 * | A day closes | The calendar tile of the day you left |
 * | Stepping days | Stays on the arrow; the day title if that arrow became disabled |
 * | A post opens | The centre picture |
 * | A post closes | That member's tile |
 * | Any fade (profile, back, composer, home) | The new screen's title |
 * | A prompt opens | Keep editing |
 * | Keep editing | The control that raised the prompt (Cancel, Delete or Home) |
 * | Profile editing starts (this build) | The picture button, or the name field when there is none |
 * | Profile editing ends (this build) | Edit profile |
 * | A widget is added without a text field opening (this build) | That widget |
 * | The selected widget is removed (this build) | The + of the island |
 */

const stage = (): ParentNode => document.querySelector('[data-v1-stage]') ?? document;
const find = (selector: string): HTMLElement | null => stage().querySelector<HTMLElement>(selector);
/** Focus an element if it is there and can take focus; reports whether it did. */
function focus(selector: string): boolean {
  const element = find(selector);
  if (!element || (element instanceof HTMLButtonElement && element.disabled)) return false;
  element.focus({ preventScroll: true });
  return document.activeElement === element;
}
const title = () => focus('[data-v1-title]');
/** Whether focus is nowhere useful: on the page itself, or on something that can no longer be used. */
function lost(): boolean {
  const active = document.activeElement;
  if (!active || active === document.body) return true;
  if (active instanceof HTMLButtonElement && active.disabled) return true;
  return !active.isConnected;
}

type Raised = '[data-v1-cancel]' | '[data-v1-delete]' | 'button[aria-label^="Home"]';

export function FocusManager() {
  const st = useAppState();
  const prev = useRef<AppState>(st);
  /** A focus move that has to wait until a fade or a closing choreography has finished. */
  const pending = useRef<null | (() => boolean)>(null);
  const raised = useRef<Raised>('[data-v1-cancel]');

  useEffect(() => {
    const was = prev.current;
    prev.current = st;
    if (was === st) return;
    const docOf = (s: AppState) => (s.view === 'compose' ? s.draft : s.pe);
    const settled = !st.lv && !st.cd && st.pp !== 'out' && st.pp !== 'in-pre';

    // Prompts.
    const asking = !!st.cAsk || st.peAsk, wasAsking = !!was.cAsk || was.peAsk;
    if (asking && !wasAsking) {
      raised.current = st.homeAfter ? 'button[aria-label^="Home"]' : st.cAsk === 'delete' ? '[data-v1-delete]' : '[data-v1-cancel]';
      focus('[data-v1-keep]');
      return;
    }
    if (!asking && wasAsking && st.view === was.view && !st.lv && (st.view === 'compose' ? !!st.draft && st.cStatus === 'idle' : !!st.pe)) {
      if (!focus(raised.current)) focus('[data-v1-cancel]');
      return;
    }

    // Profile editing starts and ends.
    if (st.view === 'profile' && was.view === 'profile') {
      if (st.pe && !was.pe) { if (!focus('button[data-v1-picture]')) focus('input'); return; }
      if (!st.pe && was.pe && !st.lv) { focus('[data-v1-edit-profile]'); return; }
    }

    // Widgets added and removed while editing.
    const doc = docOf(st), before = docOf(was);
    if (doc && before && st.view === was.view) {
      if (doc.items.length > before.items.length && st.sel && !st.txt && !before.items.some((item) => item.id === st.sel)) { focus(`[data-v1-centre-of="${st.sel}"]`); return; }
      if (doc.items.length < before.items.length && was.sel && !doc.items.some((item) => item.id === was.sel) && lost()) { focus('[data-v1-add]'); return; }
    }

    // Navigation.
    if (st.view !== was.view || st.profileOf !== was.profileOf || (st.view === 'day' && st.date !== was.date)) {
      if (st.view === 'day' && was.view === 'cal') pending.current = title;
      else if (st.view === 'cal' && was.view === 'day') { const date = was.date; pending.current = () => focus(`[data-date="${date}"]`) || title(); }
      else if (st.view === 'person' && was.view === 'day') pending.current = () => focus('[data-v1-centre]');
      else if (st.view === 'day' && was.view === 'person' && st.date === was.date) { const id = was.person; pending.current = () => focus(`[data-v1-square="${id ?? ''}"]`) || title(); }
      else if (st.view === 'day' && was.view === 'day') pending.current = () => (lost() ? title() : true);
      else if (st.view === 'person' && was.view === 'compose') pending.current = () => focus('[data-v1-centre]');
      else pending.current = title;
    }
    if (pending.current && (settled || st.view === 'day' && was.view === 'cal' || st.view === 'person' && was.view === 'day')) {
      const move = pending.current;
      pending.current = null;
      // After this render's own focus work (autofocus, removed elements).
      requestAnimationFrame(() => { move(); });
    }
  });
  return null;
}
